<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import api from '@/services/api';
import Swal from 'sweetalert2';
import logo from "@/assets/img/fondotam-blanco.png";

const loading = ref(true);
const intervenciones = ref([]);
const filtros = reactive({
  codigo_tamep: '',
  tipo: '',
});

// --- LÓGICA DE PROCESAMIENTO DE DATOS ---
const procesarDatos = (data) => {
  return data.map(item => {
    // Si no hay fecha de inicio o fin, usamos fechas seguras para no lanzar NaN
    const inicioStr = item.fecha_inicio || item.created_at || new Date().toISOString();
    const finStr = item.fecha_fin || item.fecha_reparacion || new Date().toISOString();

    const inicio = new Date(inicioStr);
    const fin = new Date(finStr);

    // Calcular duración en horas, previniendo valores negativos si las fechas están invertidas
    const duracionHrs = Math.max(0, (fin - inicio) / (1000 * 60 * 60));
    
    return {
      ...item,
      duracion: duracionHrs.toFixed(2),
      fecha_formateada: fin.toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      // Tolerancia por si el backend mandó entero en vez de objeto JSON en la lectura
      uso_hrs: item.lectura_uso_equipo?.parsedValue || item.lectura_uso_equipo || 0
    };
  });
};

// --- KPIs ESTADÍSTICOS ---
const stats = computed(() => {
  if (intervenciones.value.length === 0) return { mttr: 0, total: 0, hardware: 0 };
  
  const total = intervenciones.value.length;
  const sumaMTTR = intervenciones.value.reduce((acc, curr) => acc + parseFloat(curr.duracion), 0);
  const hardwareCount = intervenciones.value.filter(i => i.categoria_falla === 'Hardware').length;

  return {
    total,
    mttr_avg: (sumaMTTR / total).toFixed(2),
    hardware_pct: ((hardwareCount / total) * 100).toFixed(0),
    efectividad: intervenciones.value.filter(i => i.estado_post_intervencion === 'Bueno').length
  };
});

const cargarHistorial = async () => {
  loading.value = true;
  try {
    // Para evitar que Django rechace con error 400 por filtros en blanco (ej. ?codigo_tamep=)
    const parametrosLimpios = {};
    if (filtros.codigo_tamep && filtros.codigo_tamep.trim() !== '') {
      parametrosLimpios.codigo_tamep = filtros.codigo_tamep.trim();
    }
    if (filtros.tipo && filtros.tipo.trim() !== '') {
      parametrosLimpios.tipo = filtros.tipo.trim();
    }

    const res = await api.get('intervenciones/', { params: parametrosLimpios });
    // Procesamos los datos que nos dio el backend
    intervenciones.value = procesarDatos(res.data.results || res.data);
  } catch (e) {
    Swal.fire('Error', 'No se pudo conectar con el servidor', 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(cargarHistorial);
</script>

<template>
  <div class="container-fluid py-4">
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card card-stats header-tamep shadow border-0 card-custom">
          <div class="card-body p-3">
            <p class="text-white text-xs text-uppercase mb-0 opacity-8">MTTR (Tiempo de Reparación)</p>
            <h4 class="text-white font-weight-bolder mb-0">{{ stats.mttr_avg }} <small class="text-sm">hrs</small></h4>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card card-stats bg-white shadow card-custom">
          <div class="card-body p-3">
            <p class="text-secondary text-xs text-uppercase mb-0">Fallas de Hardware</p>
            <h4 class="text-dark font-weight-bolder mb-0">{{ stats.hardware_pct }}%</h4>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-lg border-radius-xl overflow-hidden card-custom">
      <div class="header-tamep px-4 py-3 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="logo-container-left me-3 p-1 rounded-lg shadow-sm" style="border-radius: 8px; background-color: #ffffff !important;">
            <img :src="logo" class="logo-img p-1" style="max-height: 40px;">
          </div>
          <div class="border-start ps-3 border-white opacity-8">
            <h6 class="text-white font-weight-bolder text-uppercase mb-0">Historial Técnico Detallado</h6>
            <small class="text-white opacity-8">Registro corporativo de mantenimientos e incidentes</small>
          </div>
        </div>
        <div class="d-flex gap-2 align-items-center">
          <input type="text" v-model="filtros.codigo_tamep" @keyup.enter="cargarHistorial" class="form-control form-control-sm border-0 shadow-sm" placeholder="Buscar TAMEP..." style="min-width: 150px;">
          <button @click="cargarHistorial" class="btn btn-sm btn-warning text-dark font-weight-bolder mb-0 shadow-sm">
            <i class="fas fa-search me-1"></i> Filtrar
          </button>
        </div>
      </div>
      <div class="card-body bg-section-custom px-0 pt-0 pb-2">
        <div class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead class="bg-gray-100">
              <tr>
                <th class="text-xxs font-weight-bolder opacity-7 ps-4">Finalización / Técnico</th>
                <th class="text-xxs font-weight-bolder opacity-7 ps-2">Categoría / Diagnóstico</th>
                <th class="text-center text-xxs font-weight-bolder opacity-7">Uso Equipo</th>
                <th class="text-center text-xxs font-weight-bolder opacity-7">MTTR (hrs)</th>
                <th class="text-center text-xxs font-weight-bolder opacity-7">Estado Final</th>
                <th class="text-center text-xxs font-weight-bolder opacity-7">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in intervenciones" :key="item.id">
                <td class="ps-4">
                  <div class="d-flex flex-column">
                    <span class="text-xs font-weight-bold">{{ item.fecha_formateada }}</span>
                    <span class="text-xxs text-secondary">{{ item.tecnico_nombre }}</span>
                  </div>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="text-xs text-dark font-weight-bold">
                       <i class="fas" :class="item.categoria_falla === 'Hardware' ? 'fa-microchip' : 'fa-code'"></i>
                       {{ item.categoria_falla }}
                    </span>
                    <span class="text-xxs text-wrap" style="max-width: 200px;">{{ item.diagnostico_previo }}</span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="text-xs font-weight-bold text-primary">{{ item.uso_hrs }} hrs</span>
                </td>
                <td class="text-center">
                  <span class="text-xs font-weight-bold" :class="item.duracion > 2 ? 'text-warning' : 'text-success'">
                    {{ item.duracion }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="badge badge-sm" :class="item.estado_post_intervencion === 'Bueno' ? 'bg-gradient-success' : 'bg-gradient-danger'">
                    {{ item.estado_post_intervencion }}
                  </span>
                </td>
                <td class="text-center">
                  <button class="btn btn-link text-dark text-sm mb-0 px-0">
                    <i class="fas fa-file-pdf text-lg me-1"></i> Reporte
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-transition { transition: all 0.3s ease; }
.card-stats { border-radius: 12px; border: none; }
.text-xxs { font-size: 0.65rem; }
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.table td, .table th { white-space: normal !important; }

.card-custom { background-color: #ffffff; transition: background-color 0.3s ease, box-shadow 0.3s ease; }
.bg-section-custom { background-color: #f8f9fa; }
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom {
  background-color: #1e1e1e !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #2d2d2d !important;
}

.dark-version .card-stats, .dark-mode .card-stats {
  background-color: #1e1e1e !important;
  border: 1px solid #333 !important;
}

.dark-version .bg-section-custom, .dark-mode .bg-section-custom {
  background-color: #121212 !important;
}

.dark-version .bg-gray-100, .dark-mode .bg-gray-100 {
  background-color: #2a2a2a !important;
  border: 1px solid #333 !important;
}

.dark-version .table, .dark-mode .table {
  color: #e0e0e0 !important;
}

.dark-version .table thead th, .dark-mode .table thead th {
  background-color: #2a2a2a !important;
  color: #ffffff !important;
  border-bottom: 1px solid #444 !important;
}

.dark-version .table td, .dark-mode .table td {
  border-bottom: 1px solid #333 !important;
}

.dark-version .form-control, .dark-mode .form-control {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.dark-version .form-control:focus, .dark-mode .form-control:focus {
  border-color: #4dabf7 !important;
}

.dark-version .text-dark, .dark-mode .text-dark { color: #ffffff !important; }
.dark-version .text-secondary, .dark-mode .text-secondary { color: #aaaaaa !important; }
</style>